/**
 * Integrytas Group - User-Defined Column Engine (UDC)
 * Page: UDC.LAYOUT
 *
 * Admin console for screen placement of user-defined columns. Pick a DSE-enabled
 * entity + workspace, assign each custom field a tab/section/order/col-span and
 * (optionally) a control override, then see a live preview rendered by the same
 * <UdcCustomFields> component the screens use.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { UdcCustomFields } from '../../components/common/UdcCustomFields';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';

interface EntityRow { entity_name: string }
interface HostRow { page_code: string; label: string | null }
interface AttrRow { dse_attribute_def_id: number; attribute_key: string; label: string | null; data_type: string }
interface LayoutRow {
  udc_field_layout_id?: number; dse_attribute_def_id: number; workspace_code: string; page_code: string;
  tab_code: string | null; section_code: string | null; display_order: number; col_span: number;
  control_override: string | null; placeholder: string | null; help_text: string | null; is_visible: 'Y' | 'N';
  show_in_grid: 'Y' | 'N'; show_in_filter: 'Y' | 'N';
}
type Merged = AttrRow & { layout: LayoutRow };

const CONTROLS = ['', 'TEXT', 'TEXTAREA', 'NUMBER', 'DATE', 'DATETIME', 'CHECKBOX', 'SELECT', 'RADIO', 'MODAL_LOOKUP'];

const LAYOUT_GRID_COLS: GridColumnDef[] = [
  { key: 'attribute_key', header: 'Field',       defaultWidth: 180 },
  { key: 'data_type',     header: 'Type',        defaultWidth: 90  },
  { key: 'tab_code',      header: 'Tab',         defaultWidth: 100 },
  { key: 'section_code',  header: 'Section',     defaultWidth: 120 },
  { key: 'display_order', header: 'Order',       defaultWidth: 70  },
  { key: 'col_span',      header: 'Span',        defaultWidth: 70  },
  { key: 'control_override', header: 'Control',  defaultWidth: 120 },
  { key: 'placeholder',   header: 'Placeholder', defaultWidth: 140 },
  { key: 'is_visible',    header: 'Form',        defaultWidth: 56  },
  { key: 'show_in_grid',  header: 'Grid',        defaultWidth: 56  },
  { key: 'show_in_filter', header: 'Filter',     defaultWidth: 56  },
];

export interface UdcLayoutPageProps { session: AuthSession }

export function UdcLayoutPage({ session }: UdcLayoutPageProps) {
  return (
    <PersonalizationProvider pageCode="UDC.LAYOUT" session={session}>
      <UdcLayoutPageInner session={session} />
    </PersonalizationProvider>
  );
}

function UdcLayoutPageInner({ session }: UdcLayoutPageProps) {
  const { t } = useTranslation();
  const actor = getForensicActorId(session);
  const [entities, setEntities] = useState<string[]>([]);
  const [entity, setEntity] = useState('');
  const [pages, setPages] = useState<HostRow[]>([]);   // candidate pages for the entity
  const [page, setPage]   = useState('');               // the page being laid out
  const [ws, setWs] = useState('');
  const [rows, setRows] = useState<Merged[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Record<string, unknown>>({});
  const [previewKey, setPreviewKey] = useState(0);
  const [filter, setFilter] = useState('');

  useEffect(() => { void (async () => {
    const { data } = await apiDb.from('dse_entity').select('entity_name').eq('is_enabled', 'Y').order('entity_name');
    setEntities(((data ?? []) as EntityRow[]).map(r => r.entity_name));
  })(); }, []);

  // Candidate pages for the chosen entity (registered hosts).
  useEffect(() => {
    if (!entity) { setPages([]); setPage(''); return; }
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb.from('udc_host').select('page_code, label').eq('entity_name', entity).order('label');
      if (cancelled) return;
      const list = (data ?? []) as HostRow[];
      setPages(list);
      setPage(prev => (list.some(h => h.page_code === prev) ? prev : (list[0]?.page_code ?? '')));
    })();
    return () => { cancelled = true; };
  }, [entity]);

  const load = async (ent: string, workspace: string, pageCode: string) => {
    if (!ent || !pageCode) { setRows([]); return; }
    setBusy(true); setError(null);
    try {
      const a = await apiDb.from('dse_attribute_def').select('dse_attribute_def_id, attribute_key, label, data_type').eq('entity_name', ent).eq('is_active', 'Y').order('sort_order').order('attribute_key');
      if (a.error) throw a.error;
      const attrs = (a.data ?? []) as AttrRow[];
      const ids = attrs.map(x => x.dse_attribute_def_id);
      let layouts: LayoutRow[] = [];
      if (ids.length) {
        const l = await apiDb.from('udc_field_layout').select('udc_field_layout_id, dse_attribute_def_id, workspace_code, page_code, tab_code, section_code, display_order, col_span, control_override, placeholder, help_text, is_visible, show_in_grid, show_in_filter').in('dse_attribute_def_id', ids).eq('workspace_code', workspace).eq('page_code', pageCode);
        if (l.error) throw l.error;
        layouts = (l.data ?? []) as LayoutRow[];
      }
      setRows(attrs.map(at => ({
        ...at,
        layout: layouts.find(x => x.dse_attribute_def_id === at.dse_attribute_def_id) ?? {
          dse_attribute_def_id: at.dse_attribute_def_id, workspace_code: workspace, page_code: pageCode,
          tab_code: null, section_code: null, display_order: 0, col_span: 6, control_override: null, placeholder: null, help_text: null,
          is_visible: 'N',   // default OFF — a field appears on a page only when ticked here
          show_in_grid: 'N', show_in_filter: 'N',
        },
      })));
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void load(entity, ws, page); }, [entity, ws, page]);

  const patch = (i: number, p: Partial<LayoutRow>) => setRows(rs => rs.map((r, j) => j === i ? { ...r, layout: { ...r.layout, ...p } } : r));

  // §17.B13: expand/contract related-children. A layout row is a single field
  // placement with no loaded child collection, so the control renders inert.
  const detail = useExpandedRows<number>();
  // TODO: wire relations — a field-placement row loads no children.
  const buildRelatedGroups = (): RelatedChildGroup[] => [];

  const saveRow = async (m: Merged) => {
    const l = m.layout;
    const { error: e } = await apiDb.from('udc_field_layout').upsert({
      dse_attribute_def_id: l.dse_attribute_def_id, workspace_code: ws, page_code: page,
      tab_code: l.tab_code?.trim() || null, section_code: l.section_code?.trim() || null,
      display_order: l.display_order, col_span: l.col_span, control_override: l.control_override || null,
      placeholder: l.placeholder?.trim() || null, help_text: l.help_text?.trim() || null, is_visible: l.is_visible,
      show_in_grid: l.show_in_grid, show_in_filter: l.show_in_filter, modified_by: actor,
    }, { onConflict: 'dse_attribute_def_id,workspace_code,page_code' });
    if (e) setError(e.message);
  };

  const refreshPreview = () => setPreviewKey(k => k + 1);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(m => m.attribute_key.toLowerCase().includes(q) || (m.label ?? '').toLowerCase().includes(q));
  }, [rows, filter]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('udc.title', 'Screen Layout')}</span>
        <select value={entity} onChange={e => setEntity(e.target.value)} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
          <option value="">{t('udc.select_entity', 'Select entity…')}</option>
          {entities.map(en => <option key={en} value={en}>{en}</option>)}
        </select>
        <span className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{t('udc.on_page', 'on page')}</span>
        <select value={page} onChange={e => setPage(e.target.value)} disabled={!entity || pages.length === 0} className="h-7 rounded border border-csm-border px-1 text-csm-xs disabled:opacity-50">
          {pages.length === 0 && <option value="">{t('udc.no_hosts', '(no screens host this entity)')}</option>}
          {pages.map(p => <option key={p.page_code} value={p.page_code}>{p.label ?? p.page_code}</option>)}
        </select>
        <input value={ws} onChange={e => setWs(e.target.value)} placeholder={t('udc.workspace_placeholder', 'workspace (blank = all)')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />
        <button onClick={() => void load(entity, ws, page)} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('udc.reload', 'Reload')}</button>
        <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('udc.filter_fields', 'Filter fields…')} className="h-7 w-44 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none" />
        {busy && <span className="text-csm-xs text-csm-watermark">{t('udc.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="min-h-0 flex-1 overflow-auto p-3">
          {error && (
            <ErrorBanner
              message={t('udc.load_failed', "Couldn't complete the operation.")}
              detail={error}
              retry_label={t('common.retry', 'Retry')}
              on_retry={() => void load(entity, ws, page)}
              details_label={t('common.details', 'Details')}
            />
          )}
          {!entity && <div className="text-csm-sm text-csm-watermark">{t('udc.empty_no_entity', 'Select a DSE-enabled entity, then a screen, to choose which of its custom fields appear on that screen. (Define fields in Shadow-Field Registry.)')}</div>}
          {entity && pages.length === 0 && <div className="text-csm-sm text-csm-watermark">{t('udc.empty_no_hosts', 'No screens are registered to host this entity’s custom fields yet.')}</div>}
          {entity && page && (
            <div className="overflow-x-auto">
              <PersonalizedGrid componentKey="grid" columns={LAYOUT_GRID_COLS}>
                {(visibleCols, widths) => (
                  <table className="w-max border-collapse text-csm-sm">
                    <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                      <th className="w-7 px-1 py-1" title={t('common.related.header', 'Related records')} />
                      {visibleCols.map(col => (
                        <th key={col.key} className="px-1 py-1" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('udc.col.' + col.key, col.header)}</th>
                      ))}
                      <th className="px-1 py-1"></th>
                    </tr></thead>
                    <tbody>
                      {filtered.map(m => {
                        const i = rows.indexOf(m);
                        return (
                          <Fragment key={m.dse_attribute_def_id}>
                          <tr className="border-b border-csm-border-soft">
                            <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                              <RelatedExpandButton open={detail.isOpen(m.dse_attribute_def_id)} hasChildren={false} onToggle={() => detail.toggle(m.dse_attribute_def_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                            </td>
                            {visibleCols.map(col => (
                              <td key={col.key} className="px-1 py-1" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                                {col.key === 'attribute_key'    && (<><span className="font-mono text-csm-xs">{m.attribute_key}</span><div className="text-csm-2xs text-csm-watermark">{m.label}</div></>)}
                                {col.key === 'data_type'        && <span className="text-csm-2xs text-csm-ink-soft">{m.data_type}</span>}
                                {col.key === 'tab_code'         && <input value={m.layout.tab_code ?? ''} onChange={e => patch(i, { tab_code: e.target.value })} onBlur={() => void saveRow(rows[i])} className="h-7 w-20 rounded border border-csm-border px-1 text-csm-2xs" />}
                                {col.key === 'section_code'     && <input value={m.layout.section_code ?? ''} onChange={e => patch(i, { section_code: e.target.value })} onBlur={() => void saveRow(rows[i])} className="h-7 w-24 rounded border border-csm-border px-1 text-csm-2xs" />}
                                {col.key === 'display_order'    && <input type="number" value={m.layout.display_order} onChange={e => patch(i, { display_order: Number(e.target.value) })} onBlur={() => void saveRow(rows[i])} className="h-7 w-12 rounded border border-csm-border px-1 text-csm-2xs" />}
                                {col.key === 'col_span'         && <input type="number" min={1} max={12} value={m.layout.col_span} onChange={e => patch(i, { col_span: Number(e.target.value) })} onBlur={() => void saveRow(rows[i])} className="h-7 w-12 rounded border border-csm-border px-1 text-csm-2xs" />}
                                {col.key === 'control_override' && <select value={m.layout.control_override ?? ''} onChange={e => patch(i, { control_override: e.target.value || null })} onBlur={() => void saveRow(rows[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{CONTROLS.map(c => <option key={c} value={c}>{c || t('udc.control_auto', '(auto)')}</option>)}</select>}
                                {col.key === 'placeholder'      && <input value={m.layout.placeholder ?? ''} onChange={e => patch(i, { placeholder: e.target.value })} onBlur={() => void saveRow(rows[i])} className="h-7 w-28 rounded border border-csm-border px-1 text-csm-2xs" />}
                                {col.key === 'is_visible'       && <input type="checkbox" checked={m.layout.is_visible === 'Y'} onChange={e => { patch(i, { is_visible: e.target.checked ? 'Y' : 'N' }); }} onBlur={() => void saveRow(rows[i])} title={t('udc.tooltip.show_in_form', 'Show in the edit form')} />}
                                {col.key === 'show_in_grid'     && <input type="checkbox" checked={m.layout.show_in_grid === 'Y'} onChange={e => { patch(i, { show_in_grid: e.target.checked ? 'Y' : 'N' }); }} onBlur={() => void saveRow(rows[i])} title={t('udc.tooltip.show_in_grid', 'Show as a list/grid column')} />}
                                {col.key === 'show_in_filter'   && <input type="checkbox" checked={m.layout.show_in_filter === 'Y'} onChange={e => { patch(i, { show_in_filter: e.target.checked ? 'Y' : 'N' }); }} onBlur={() => void saveRow(rows[i])} title={t('udc.tooltip.show_in_filter', 'Show as a filter-bar facet')} />}
                              </td>
                            ))}
                            <td className="px-1 py-1"><button onClick={() => void saveRow(rows[i])} className="text-csm-xs text-primary hover:underline">{t('udc.save', 'save')}</button></td>
                          </tr>
                          {detail.isOpen(m.dse_attribute_def_id) && (
                            <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups()} session={session} /></td></tr>
                          )}
                          </Fragment>
                        );
                      })}
                      {filtered.length === 0 && entity && (
                        <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3">
                          <EmptyState variant={filter.trim() ? 'no-match' : 'no-data'} title={t('udc.empty_no_fields', 'No custom fields defined for this entity. Add them in Shadow-Field Registry.')} />
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </PersonalizedGrid>
            </div>
          )}
        </div>

        {/* Live preview */}
        {entity && (
          <div className="w-[28rem] shrink-0 overflow-auto border-l border-csm-border bg-csm-surface-alt p-3">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-csm-2xs font-semibold uppercase tracking-wide text-csm-ink-soft">{t('udc.live_preview', 'Live preview')}</span>
              <button onClick={refreshPreview} className="h-6 rounded border border-csm-border bg-white px-2 text-csm-2xs hover:bg-white">{t('udc.refresh', 'Refresh')}</button>
            </div>
            <div className="rounded border border-csm-border bg-white p-3">
              <UdcCustomFields key={`${page}:${previewKey}`} entity={entity} pageCode={page} workspaceCode={ws} value={preview} onChange={setPreview} />
            </div>
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-white p-2 text-csm-2xs text-csm-ink-soft">{JSON.stringify(preview, null, 2)}</pre>
            <p className="mt-1 text-csm-2xs text-csm-watermark">{t('udc.preview_note', 'Edits save on blur; click Refresh to re-hydrate the preview from the saved layout.')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
